/* eslint-disable complexity */
import { validBroadcastStateCodes } from './BroadcastStateCodes';
import { StateInfo, Broadcast, Segment } from './types';

const isStateInfo = (arg: any): arg is StateInfo => {
  return (
    arg &&
    typeof arg === 'object' &&
    arg.code &&
    typeof arg.code === 'string' &&
    validBroadcastStateCodes.includes(arg.code) &&
    arg.user_info_meaning &&
    typeof arg.user_info_meaning === 'string' &&
    (arg.user_info_details ? Array.isArray(arg.user_info_details) : true) &&
    (arg.description_log ? typeof arg.description_log === 'string' : true)
  );
};

const isSegment = (arg: any): arg is Segment => {
  return (
    arg &&
    typeof arg === 'object' &&
    arg.start_time &&
    typeof arg.start_time === 'string' &&
    (arg.end_time ? typeof arg.end_time === 'string' : true) // Some segments may not have an end time.
  );
};

const isSegmentArray = (arg: any[]): arg is Segment[] => {
  return arg && Array.isArray(arg) && arg.every(isSegment);
};

export const isBroadcast = (arg: any): arg is Broadcast => {
  return (
    arg &&
    typeof arg === 'object' &&
    arg.id &&
    typeof arg.id === 'number' &&
    (arg.stream_key_public_id ? typeof arg.stream_key_public_id === 'string' : true) &&
    arg.display_name &&
    typeof arg.display_name === 'string' &&
    arg.est_start_time &&
    typeof arg.est_start_time === 'string' &&
    arg.est_end_time &&
    typeof arg.est_end_time === 'string' &&
    (arg.fwd_emails ? Array.isArray(arg.fwd_emails) : true) &&
    (arg.destination ? typeof arg.destination === 'string' : true) &&
    arg.created_at &&
    typeof arg.created_at === 'string' &&
    (arg.is_active ? typeof arg.is_active === 'boolean' : true) && // Backwards compatibility for Kappi.
    (arg.is_streaming ? typeof arg.is_streaming === 'boolean' : true) && // Ditto.
    (arg.state ? isStateInfo(arg.state) : true) &&
    (arg.segments ? isSegmentArray(arg.segments) : true)
  );
};

export const isBroadcastArray = (arg: any[]): arg is Broadcast[] => {
  return arg && Array.isArray(arg) && arg.every(isBroadcast);
};

/*
  == Kappi broadcast data return schema. [As of 21.02.24] ==
  Required fields are always present in payload. Others are optional.

  id                    required
  stream_key_public_id
  display_name          required
  est_start_time        required
  est_end_time          required
  fwd_emails
  destination
  created_at            required

  state                 required  [only present in newer versions of Kappi]

  is_active             required  [only present in older versions of Kappi]
  is_streaming                    [only present in older versions of Kappi]

  segments

  == Kappi broadcast data return schema examples. ==

  === Broadcast list item ===
    {
        "created_at": "Tue, 23 May 2023 11:35:18 GMT",
        "destination": "rtmp://192.168.1.7:5550/hr",
        "display_name": "Prufustreymi HR",
        "est_end_time": "Tue, 23 May 2023 12:25:00 GMT",
        "est_start_time": "Tue, 23 May 2023 11:40:00 GMT",
        "fwd_emails": [
            "julli@tiro.is"
        ],
        "id": 53,
        "state": {
            "code": "FINISHED",
            "description_log": "Broadcast has gone past its timeframe and concluded its final segment.",
            "user_info_details": [
                "Lokað hefur verið fyrir streymi.",
                "Til að fara að rauntímatexta á ný getur þú búið til nýja útsendingu."
            ],
            "user_info_meaning": "Útsendingu lokið"
        }
    }

  === Broadcast details ===
    {
        "created_at": "Tue, 23 May 2023 11:35:18 GMT",
        "destination": "rtmp://192.168.1.7:5550/hr",
        "display_name": "Prufustreymi HR",
        "est_end_time": "Tue, 23 May 2023 12:25:00 GMT",
        "est_start_time": "Tue, 23 May 2023 11:40:00 GMT",
        "fwd_emails": [
            "julli@tiro.is"
        ],
        "id": 53,
        "segments": [
            {
                "end_time": null,
                "start_time": "Tue, 23 May 2023 11:40:33 GMT"
            },
            {
                "end_time": "Tue, 23 May 2023 11:44:14 GMT",
                "start_time": "Tue, 23 May 2023 11:42:46 GMT"
            }
        ],
        "state": {
            "code": "FINISHED",
            "description_log": "Broadcast has gone past its timeframe and concluded its final segment.",
            "user_info_details": [
                "Lokað hefur verið fyrir streymi.",
                "Til að fara að rauntímatexta á ný getur þú búið til nýja útsendingu."
            ],
            "user_info_meaning": "Útsendingu lokið"
        },
        "stream_key_public_id": "stream_key_485eb653-5be4-457f-a09e-ca44a88e966c"
    }
*/
